// @ts-ignore
import { escapeCSSSelector } from 'common/js/utils/dom';

/**
 * Same-page anchor links (in the content, TOC, …). A jump up reveals the
 * header (header-navigation.ts) right over the target, so the target is placed below it;
 * a jump down hides the header, so only a small gap is left.
 */

/** Space between the target and the header (or the top of the viewport when it's hidden). */
const TARGET_GAP = 16;

/**
 * The sticky mobile TOC bar (table-of-contents-mobile.twig) stays fixed below the header's spot
 * even when the header is hidden, so a target must always clear its bottom edge.
 */
const getMobileTocBarBottom = (): number => {
    const bar = document.querySelector<HTMLElement>('[data-js-table-of-contents="mobile"]');

    if (!bar || getComputedStyle(bar).display === 'none') {
        return 0;
    }

    const rootStyle = getComputedStyle(document.documentElement);
    const barHeight = parseFloat(rootStyle.getPropertyValue('--table-of-contents-collapsed-height')) || 0;

    return (parseFloat(getComputedStyle(bar).top) || 0) + barHeight;
};

const findTarget = (link: HTMLAnchorElement): HTMLElement | null => {
    if (link.hash === '' || link.origin !== window.location.origin || link.pathname !== window.location.pathname) {
        return null;
    }

    try {
        return document.querySelector<HTMLElement>(escapeCSSSelector(link.hash));
    } catch (err) {
        return null;
    }
};

document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    const link = (event.target as Element | null)?.closest?.('a[href*="#"]');

    if (!(link instanceof HTMLAnchorElement)) {
        return;
    }

    const target = findTarget(link);

    if (!target) {
        return;
    }

    event.preventDefault();

    const headerHeight = document.querySelector<HTMLElement>('[data-js-header]')?.offsetHeight || 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const scrollsUp = targetTop - TARGET_GAP < window.scrollY;
    // The header only hides once the page is scrolled past its own height.
    const headerVisible = scrollsUp || targetTop - TARGET_GAP <= headerHeight;
    const covered = Math.max(headerVisible ? headerHeight : 0, getMobileTocBarBottom());

    window.scrollTo({ top: Math.max(targetTop - covered - TARGET_GAP, 0) });
    window.history.pushState(null, '', link.hash);
});

export {};
